/**
 * V1 URL-backed omnisearch store (useSyncExternalStore).
 *
 * Hook exports are deprecated — use `hooks/omnisearch-v2` instead.
 * Utility re-exports (`parseFilterKeys`, `extractOmnisearchValue`, etc.) remain
 * shared infrastructure.
 */
import { useSyncExternalStore, useMemo, startTransition } from "react";
import {
  arraysEqual,
  buildFilterFromKeys as sharedBuildFilterFromKeys,
  extractOmnisearchValue as sharedExtractOmnisearchValue,
  filterKeysEqual,
  FilterKeys,
  INITIAL_FILTER_KEYS,
  parseFilterKeys,
} from "utils/omnisearchFilters";
import {
  addOrUpdateKeyInOmnisearch,
  breakApartOmnisearch,
  parseOmnisearchTokens,
  removeKeyInOmnisearch,
} from "utils/omnisearchUtils";
import { installHistoryInterceptors, subscribeToURL } from "utils/urlInterceptor";

// Re-export shared symbols so the existing public API of this module is preserved.
export type { FilterKeys };
export { INITIAL_FILTER_KEYS, parseFilterKeys, filterKeysEqual, installHistoryInterceptors };
export const extractOmnisearchValue = sharedExtractOmnisearchValue;

interface OmnisearchState {
  omnisearch: string;
  filterKeys: FilterKeys;
}

// ============================================================================
// URL-based store using useSyncExternalStore
// ============================================================================

// Cache for referential stability
let cachedState: OmnisearchState = {
  omnisearch: "",
  filterKeys: INITIAL_FILTER_KEYS,
};

/**
 * Get current snapshot by reading URL.
 * Called synchronously during render by useSyncExternalStore.
 * Returns cached state if URL hasn't changed (referential stability).
 */
function getSnapshot(): OmnisearchState {
  const params = new URLSearchParams(window.location.search);
  const omnisearch = params.get("omnisearch") ?? "";

  // Return cached state if omnisearch hasn't changed
  if (omnisearch === cachedState.omnisearch) {
    return cachedState;
  }

  // Parse new filter keys
  const newFilterKeys = parseFilterKeys(omnisearch);

  // Maintain filterKeys reference stability if content is equal
  const filterKeysAreEqual = filterKeysEqual(cachedState.filterKeys, newFilterKeys);
  const stableFilterKeys = filterKeysAreEqual ? cachedState.filterKeys : newFilterKeys;

  // Update cache
  cachedState = {
    omnisearch,
    filterKeys: stableFilterKeys,
  };

  return cachedState;
}

/**
 * Server snapshot - empty state for SSR.
 */
function getServerSnapshot(): OmnisearchState {
  return {
    omnisearch: "",
    filterKeys: INITIAL_FILTER_KEYS,
  };
}

// ============================================================================
// Action creators - write to URL
// ============================================================================

/**
 * Update URL with new search params.
 * Uses replaceState to avoid polluting history with every change.
 * Wrapped in startTransition so Suspense boundaries show stale data
 * instead of falling back to skeleton during filter changes.
 */
function updateURL(newOmnisearch: string, useReplace = true) {
  startTransition(() => {
    const params = new URLSearchParams(window.location.search);

    if (newOmnisearch) {
      params.set("omnisearch", newOmnisearch);
    } else {
      params.delete("omnisearch");
    }

    const newSearch = params.toString();
    const newUrl = newSearch
      ? `${window.location.pathname}?${newSearch}`
      : window.location.pathname;

    if (useReplace) {
      history.replaceState(null, "", newUrl);
    } else {
      history.pushState(null, "", newUrl);
    }
  });
}

/**
 * Set the omnisearch value, replacing the current URL.
 */
function setOmnisearch(value: string) {
  updateURL(value, true);
}

/**
 * Set the omnisearch value immediately (same as setOmnisearch, kept for API compatibility).
 */
function setOmnisearchImmediate(value: string) {
  updateURL(value, true);
}

/**
 * Edit multiple key-value pairs in the omnisearch.
 */
function editKeyValues(keyValues: [string, string][]) {
  const current = getSnapshot().omnisearch;
  let updated = current;
  for (const [key, value] of keyValues) {
    updated = addOrUpdateKeyInOmnisearch(updated, key, value);
  }
  updateURL(updated, true);
}

/**
 * Remove a key from the omnisearch.
 */
function removeKey(key: string) {
  const current = getSnapshot().omnisearch;
  const updated = removeKeyInOmnisearch(current, key);
  updateURL(updated, true);
}

// ============================================================================
// Selector Hooks - Components subscribe to ONLY the state slices they need
// ============================================================================

/**
 * Hook to get the full omnisearch state using useSyncExternalStore.
 *
 * @deprecated Use `useOmnisearchStore` from `hooks/omnisearch-v2` instead.
 */
export function useOmnisearchStore(): OmnisearchState {
  return useSyncExternalStore(subscribeToURL, getSnapshot, getServerSnapshot);
}

/**
 * For chart components - only re-renders when filter-relevant state changes.
 *
 * @deprecated Use `useChartFilterKeys` from `hooks/omnisearch-v2` instead.
 */
export function useChartFilterKeys(): FilterKeys {
  const state = useSyncExternalStore(subscribeToURL, getSnapshot, getServerSnapshot);
  return state.filterKeys;
}

/**
 * For DataGrid - subscribes to full omnisearch string.
 *
 * @deprecated Use `useFullOmnisearch` from `hooks/omnisearch-v2` instead.
 */
export function useFullOmnisearch(): string {
  const state = useSyncExternalStore(subscribeToURL, getSnapshot, getServerSnapshot);
  return state.omnisearch;
}

/**
 * For components that need omnisearch actions.
 *
 * @deprecated Use `useOmnisearchActions` from `hooks/omnisearch-v2` instead.
 */
export function useOmnisearchActions() {
  // Memoize action references to ensure stability
  return useMemo(
    () => ({
      setOmnisearch,
      setOmnisearchImmediate,
      editKeyValues,
      removeKey,
    }),
    []
  );
}

// ============================================================================
// Helper to convert FilterKeys to GraphQL filter format
// ============================================================================

export const buildFilterFromKeys = sharedBuildFilterFromKeys;

// ============================================================================
// Exports for testing
// ============================================================================

/**
 * Reset the cache - useful for testing.
 */
export function __resetCache() {
  cachedState = {
    omnisearch: "",
    filterKeys: INITIAL_FILTER_KEYS,
  };
}

/**
 * Exports for testing internal functions.
 */
export const __testing = {
  parseFilterKeys,
  filterKeysEqual,
  arraysEqual,
  parseOmnisearchTokens,
  breakApartOmnisearch,
  addOrUpdateKeyInOmnisearch,
  removeKeyInOmnisearch,
};
